// src/utils/errorHandler.js
import axios from "axios";

// Centralized error handling utility
export const handleApiError = (error) => {
  if (axios.isAxiosError(error)) {
    // Handle Axios errors
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 400:
          return data.message || "Invalid request. Please check your input.";
        case 401:
          return data.message || "Authentication required. Please log in again.";
        case 403:
          return data.message || "Access denied. You do not have permission to perform this action.";
        case 404:
          return data.message || "The requested resource was not found.";
        case 409:
          return data.message || "This action conflicts with existing data.";
        case 422:
          return data.message || "The provided data is invalid.";
        case 429:
          return "Too many requests. Please wait a moment and try again.";
        case 500:
          return "Server error. Please try again later.";
        case 503:
          return "Service temporarily unavailable. Please try again later.";
        default:
          return data.message || `Server error (${status}). Please try again.`;
      }
    } else if (error.request) {
      // Network error
      return "Network error. Please check your internet connection and try again.";
    } else {
      // Request setup error
      return "Request error. Please try again.";
    }
  } else if (error instanceof Error) {
    // Regular JavaScript errors
    return error.message;
  } else {
    // Unknown error type
    return "An unexpected error occurred. Please try again.";
  }
};

// Retry utility for failed requests
export const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;

      // Don't retry on certain error types
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;
        if ([400, 401, 403, 404, 422].includes(status)) {
          throw error; // Don't retry client errors
        }
      }

      if (i < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)));
      }
    }
  }

  throw lastError;
};

// Network status checker
export const checkNetworkStatus = () => {
  return navigator.onLine;
};

// Error reporting (for production)
export const reportError = (error, context = {}) => {
  if (import.meta.env.MODE === "production") {
    // Example: Sentry.captureException(error, { contexts: { ...context } });
    console.error("Error reported:", error, context);
  } else {
    console.error("Error occurred:", error, context);
  }
};

// Validate response data
export const validateResponse = (response, expectedFields = []) => {
  if (!response || typeof response !== "object") {
    throw new Error("Invalid response format");
  }

  if (expectedFields.length > 0) {
    const missingFields = expectedFields.filter(
      (field) => !(field in response)
    );
    if (missingFields.length > 0) {
      throw new Error(`Missing expected fields: ${missingFields.join(", ")}`);
    }
  }

  return response;
};

export default {
  handleApiError,
  retryRequest,
  checkNetworkStatus,
  reportError,
  validateResponse,
};
